import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SEOMeta from '../components/SEOMeta';

export default function ContattiPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Compila i campi obbligatori'); return; }
    setSubmitting(true);
    // Simulate send (contact form - no backend endpoint needed for MVP)
    await new Promise(r => setTimeout(r, 800));
    toast.success(t('contact.success'));
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setSubmitting(false);
  };

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Contatti"
        description="Contatta Una Zampa in Paradiso: siamo disponibili 24/7 per assisterti. Telefono, email, o compila il modulo di contatto. Ti risponderemo con cura e nel più breve tempo possibile."
        url="/contatti"
      />
      {/* Hero */}
      <section className="hero-gradient py-12 lg:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <Mail size={14} /> Contatti
            </div>
            <h1 className="font-serif text-5xl font-semibold text-[var(--color-deep)] mb-3">{t('contact.title')}</h1>
            <p className="text-[var(--color-muted-text)] max-w-xl">{t('contact.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-py">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8">
                <h3 className="font-serif text-2xl font-semibold text-[var(--color-deep)] mb-6">Inviaci un messaggio</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('contact.name')} *</Label>
                      <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Mario Rossi" className="mt-1 border-[var(--color-border)]" data-testid="contact-name" />
                    </div>
                    <div>
                      <Label>{t('contact.email')} *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="mario@example.it" className="mt-1 border-[var(--color-border)]" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('contact.phone')}</Label>
                      <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+39 333 1234567" className="mt-1 border-[var(--color-border)]" />
                    </div>
                    <div>
                      <Label>{t('contact.subject')}</Label>
                      <Input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder="Cremazione individuale" className="mt-1 border-[var(--color-border)]" />
                    </div>
                  </div>
                  <div>
                    <Label>{t('contact.message')} *</Label>
                    <Textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Come possiamo aiutarti?" className="mt-1 border-[var(--color-border)] resize-none" rows={5} data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl btn-press" data-testid="contact-submit">
                    <Send size={16} className="mr-2" />
                    {submitting ? 'Invio...' : t('contact.submit')}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Info */}
            <motion.div initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="space-y-6">
              <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6">
                <h3 className="font-serif font-semibold text-[var(--color-deep)] mb-1">{t('contact.address')}</h3>
                <p className="text-xs text-[var(--color-muted-text)] mb-4">Una Zampa In Paradiso S.r.l.s.</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-[var(--color-sage)] mt-0.5" />
                    <div>
                      <p className="text-sm text-[var(--color-ink)]">Via Mangiacane, 11</p>
                      <p className="text-sm text-[var(--color-ink)]">12011 Borgo San Dalmazzo (CN)</p>
                      <p className="text-xs text-[var(--color-muted-text)] mt-1">P.IVA 04183140047</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-[var(--color-sage)]" />
                    <a href="tel:+393454645573" className="text-sm text-[var(--color-ink)] hover:text-[var(--color-sage)] transition-colors">+39 345 464 5573</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#25D366] fill-current flex-shrink-0" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    <a href="https://wa.me/393454645573" target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--color-ink)] hover:text-[#25D366] transition-colors font-medium">Scrivici su WhatsApp</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-[var(--color-sage)]" />
                    <a href="mailto:info@unazampaparadiso.it" className="text-sm text-[var(--color-ink)] hover:text-[var(--color-sage)] transition-colors">info@unazampaparadiso.it</a>
                  </div>
                </div>
              </div>

              <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6">
                <h3 className="font-serif font-semibold text-[var(--color-deep)] mb-4">{t('contact.hours')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-text)]">Lunedì - Venerdì</span><span className="font-medium text-[var(--color-ink)]">9:00 - 18:00</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-text)]">Sabato</span><span className="font-medium text-[var(--color-ink)]">9:00 - 14:00</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[var(--color-muted-text)]">Domenica</span><span className="font-medium text-[var(--color-ink)]">Chiuso</span></div>
                </div>
              </div>

              <div className="bg-[var(--color-terracotta-soft)] border border-[var(--color-terracotta)]/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={16} className="text-[var(--color-terracotta)]" />
                  <h4 className="font-serif font-semibold text-[var(--color-deep)]">{t('contact.emergency')}</h4>
                </div>
                <p className="text-sm text-[var(--color-muted-text)] mb-3">Siamo disponibili H24 per le emergenze.</p>
                <div className="flex flex-col gap-2">
                  <a href="tel:+393454645573" className="flex items-center gap-2 text-sm font-semibold text-[var(--color-terracotta)] hover:underline">
                    <Phone size={14} /> Chiama ora
                  </a>
                  <a href="https://wa.me/393454645573?text=Ciao,%20ho%20un'emergenza%20e%20avrei%20bisogno%20di%20assistenza." target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#25D366] hover:underline">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp Emergenze
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
